import './membership-join.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Provider, useDispatch } from 'react-redux';

import { useAuth } from '../../../auth/hooks/use-auth.hook';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import type { AccountSettings } from '../../../models/common.model';
import type { AppDispatch } from '../../../store';
import { store } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import type { Membership, ReferralLink, Tier } from '../../models/membership.model';
import { MembershipJoinFlowService } from '../../services/membership-join-flow.service';
import {
  selectCreatingContract,
  selectMemberships,
  selectReferralLink,
} from '../../store/membership.selectors';
import { loadMemberships, loadReferralLink } from '../../store/membership.thunk';
import { MembershipTier } from '../membership-tier/membership-tier.component';

export interface MembershipJoinProps {
  tierId: string;
  referralLinkId: string;
}

/**
 * Calculate redirect URL after membership join
 */
function calculateRedirectUrl(
  membership: Membership,
  tier: Tier,
  referralLink: ReferralLink
): string {
  // Use referral link URL if available, otherwise use tier's post-purchase URL
  let url = referralLink.postSignupUrl ?? getTierPostPurchaseUrl(tier);
  // Handle special case for membership-pass redirect with wallet theme
  if (url.endsWith('/apps/single/membership-pass')) {
    const hasWalletTheme =
      (!membership.contract && tier.walletTheme?.enabled) ||
      membership.contract?.tier?.walletTheme?.enabled;
    if (hasWalletTheme) {
      url = `${url}?membership-id=${membership.id}`;
    } else {
      url = getTierPostPurchaseUrl(tier);
    }
  }
  return url;
}

/**
 * Get tier's post-purchase URL or default
 */
function getTierPostPurchaseUrl(tier: Tier): string {
  return tier?.postPurchaseUrl && tier.postPurchaseUrl !== ''
    ? tier.postPurchaseUrl
    : `${window.location.origin}/apps/single/membership`;
}

const MembershipJoinComponent: FunctionComponent<MembershipJoinProps> = ({
  tierId,
  referralLinkId,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { openAuthModal } = useAuth();

  // Redux selectors
  const memberships = useAppSelector(selectMemberships);
  const referralLink = useAppSelector(selectReferralLink);
  const creatingContract = useAppSelector(selectCreatingContract);

  // Local state
  const [membership, setMembership] = useState<Membership | null>(null);
  const [tier, setTier] = useState<Tier | null>(null);
  const [accountSettings, setAccountSettings] = useState<AccountSettings | undefined>(undefined);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [showTier, setShowTier] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataProcessed, setDataProcessed] = useState(false);

  // Load data on mount
  useEffect(() => {
    dispatch(loadMemberships());
    if (referralLinkId) {
      dispatch(loadReferralLink(referralLinkId));
    }
  }, [dispatch, referralLinkId]);

  // Check if data is ready
  const dataReady = useMemo(() => {
    return memberships && memberships.length > 0 && referralLink;
  }, [memberships, referralLink]);

  // Process data when ready (replaces combineLatest pattern)
  useEffect(() => {
    if (!dataReady || dataProcessed || creatingContract) return;

    // Extract account settings
    const settings = memberships ? memberships[0]?.accountSettings : undefined;
    setAccountSettings(settings);

    // Find membership and tier
    const foundMembership = memberships
      ? memberships.find((m) => m.tiers.some((t) => t.id === tierId))
      : undefined;

    if (!foundMembership) {
      setLoading(false);
      return;
    }

    const foundTier = foundMembership.tiers.find((t) => t.id === tierId);

    if (!foundTier) {
      setLoading(false);
      return;
    }

    setMembership(foundMembership);
    setTier(foundTier);

    // Calculate redirect URL
    const calculatedRedirectUrl = calculateRedirectUrl(foundMembership, foundTier, referralLink!);
    setRedirectUrl(calculatedRedirectUrl);

    // Handle auto-join logic for free tiers or single-term tiers
    if (foundTier.freeTier || foundTier.terms?.length === 1) {
      MembershipJoinFlowService.initiateMembershipJoin(
        {
          tier: foundTier,
          referralLinkId,
          redirectUrl: calculatedRedirectUrl,
          rejectUrl: `${window.location.origin}/apps/single/membership`,
        },
        dispatch,
        openAuthModal
      );
      setLoading(false);
    } else {
      // Show tier card for paid tiers with multiple terms
      setLoading(false);
      setShowTier(true);
    }

    // Mark data as processed (replaces take(1))
    setDataProcessed(true);
  }, [
    dataReady,
    dataProcessed,
    creatingContract,
    memberships,
    referralLink,
    tierId,
    referralLinkId,
    dispatch,
    openAuthModal,
  ]);

  return (
    <>
      {/* Loading Spinner */}
      {loading && (
        <section
          className="single-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '16 / 9',
            maxHeight: '75vh',
            width: '100%',
          }}
        >
          <SpinnerComponent />
        </section>
      )}
      {/* Tier Card */}
      {!loading && showTier && membership && tier && (
        <div className="page-width single-card-grid single-mt-1 single-mb-1 single-membership-join">
          <div className="single-card-row">
            <div className="single-card-option">
              <MembershipTier
                membership={membership}
                tier={tier}
                accountSettings={accountSettings}
                redirectUrl={redirectUrl}
                showProgramName={true}
                showTierImage={true}
                showTierName={true}
                showTierDescription={true}
                showPrice={true}
                showQuickAdd={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipJoin = memo(MembershipJoinComponent);

// Wrapper with Redux Provider for standalone widget usage
export const MembershipJoinWithStore: FunctionComponent<MembershipJoinProps> = (props) => {
  return (
    <Provider store={store}>
      <MembershipJoin {...props} />
    </Provider>
  );
};
